import { SubmitHandler, useForm } from '@agrotoken-tech/ui';
import { DigitalDossierRepresentantInformationSerializer } from '@bcba/serializers';
import {
  QueryObserverResult,
  RefetchOptions,
  useMutation,
} from '@tanstack/react-query';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { UpsertExplorerDigitalDossierLegalRepresentantInformationDto } from './dto/upsertGeneralInformationDto';
import { updateLegalRepresentativesInformation } from '../../../../services/digital-dossiers/update-information';
import { useToastsHook, useTranslation } from '@bcba/react-hooks';
import { useEffect } from 'react';
import { VALIDATION_STATUSES } from '@bcba/types';
import { useDigitalDossierSectionContext } from '../../context/DigitalDossierSectionContext';
import { useDigitalDossierActionButtons } from '../../hooks/useDigitalDossierActionButtons';
import { usePendingChangesAlert } from '../../../../contexts/hooks';

const resolver = classValidatorResolver(
  UpsertExplorerDigitalDossierLegalRepresentantInformationDto,
);

const getDefaultLegalRepresentative = (
  legalRepresentative?: DigitalDossierRepresentantInformationSerializer,
) => {
  const { uuid, email } = legalRepresentative ?? {};
  return {
    uuid,
    email,
  };
};

export const useLegalRepresentativesForm = (
  data: DigitalDossierRepresentantInformationSerializer[],
  refetch: (options?: RefetchOptions | undefined) => Promise<
    QueryObserverResult<
      DigitalDossierRepresentantInformationSerializer[],
      Error // TODO: use axios error?
    >
  >,
) => {
  const { t } = useTranslation();
  const { showSuccessToast } = useToastsHook();
  const { refetchDigitalDossierStatus } = useDigitalDossierSectionContext();

  const status = data?.[0]?.status ?? VALIDATION_STATUSES.INCOMPLETE;
  const validationMessage = data?.[0]?.validationMessage;
  const isPendingChangeRequest =
    status === VALIDATION_STATUSES.PENDING_CHANGE_REQUEST;

  const defaultLegalRepresentatives = data.length
    ? data.map((legalRep) => getDefaultLegalRepresentative(legalRep))
    : [getDefaultLegalRepresentative()];

  const formMethods =
    useForm<UpsertExplorerDigitalDossierLegalRepresentantInformationDto>({
      resolver,
      defaultValues: { legalRepresentants: [{}] },
    });

  const {
    handleSubmit,
    reset,
    formState: { dirtyFields },
  } = formMethods;
  const isDirty = Boolean(Object.keys(dirtyFields).length);
  usePendingChangesAlert(isDirty);

  const { mutate, isPending } = useMutation({
    mutationFn: (
      data: UpsertExplorerDigitalDossierLegalRepresentantInformationDto,
    ) => updateLegalRepresentativesInformation(data),
    onSuccess: () => {
      refetchDigitalDossierStatus();
      showSuccessToast({
        title: t('explorer.digitalDossier.legalRepsInfo.update.success.toast'),
      });
      refetch();
    },
  });

  const onSubmit: SubmitHandler<
    UpsertExplorerDigitalDossierLegalRepresentantInformationDto
  > = (data) => {
    mutate(data);
  };

  const { actionButtons, isFormReadOnly } = useDigitalDossierActionButtons({
    status,
    isLoadingSave: isPending,
    formHasChanges: isDirty,
  });

  // This is to reset form after submit
  useEffect(() => {
    reset({
      legalRepresentants: defaultLegalRepresentatives,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return {
    formMethods,
    handleSubmit,
    onSubmit,
    status,
    validationMessage,
    isPendingChangeRequest,
    actionButtons,
    isFormReadOnly,
  };
};
